/**
 * useOnboardingStatus Hook
 *
 * Aggregates onboarding progress for a client including:
 * - Intake forms completed
 * - PAR-Q status and flags
 * - Medical clearance status
 * - Waivers signed
 * - Assessment scores
 */

import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";

export interface OnboardingStatus {
  // General status
  isComplete: boolean;
  completionPercentage: number;

  // Intake forms
  intakeFormsCompleted: number;
  intakeFormsTotal: number;

  // PAR-Q
  hasCompletedParq: boolean;
  parqHasFlags: boolean;
  parqRequiresClearance: boolean;
  latestParqResponseId: string | null;

  // Medical clearance
  hasActiveClearance: boolean;
  clearanceRequired: boolean;
  activeClearanceId: string | null;

  // Waivers
  validWaiversCount: number;
  requiredWaiversCount: number;
  hasAllRequiredWaivers: boolean;

  // Assessments
  hasCompletedAssessment: boolean;
  latestAssessmentScore: number | null;
  latestAssessmentMaxScore: number | null;
  latestAssessmentDate: string | null;
}

interface UseOnboardingStatusReturn {
  status: OnboardingStatus | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

const DEFAULT_STATUS: OnboardingStatus = {
  isComplete: false,
  completionPercentage: 0,
  intakeFormsCompleted: 0,
  intakeFormsTotal: 0,
  hasCompletedParq: false,
  parqHasFlags: false,
  parqRequiresClearance: false,
  latestParqResponseId: null,
  hasActiveClearance: false,
  clearanceRequired: false,
  activeClearanceId: null,
  validWaiversCount: 0,
  requiredWaiversCount: 0,
  hasAllRequiredWaivers: true,
  hasCompletedAssessment: false,
  latestAssessmentScore: null,
  latestAssessmentMaxScore: null,
  latestAssessmentDate: null,
};

export function useOnboardingStatus(clientId: string | null): UseOnboardingStatusReturn {
  const [status, setStatus] = useState<OnboardingStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchStatus = useCallback(async () => {
    if (!clientId) {
      setLoading(false);
      setStatus(null);
      return;
    }

    try {
      setError(null);

      // Call the database function to get onboarding progress
      const { data, error: fetchError } = await supabase.rpc("get_onboarding_progress", {
        p_client_id: clientId,
      });

      if (fetchError) throw fetchError;

      if (data) {
        // Map database response to our interface
        const onboardingData = data as {
          intake_forms_completed: number;
          intake_forms_total: number;
          has_completed_parq: boolean;
          parq_has_flags: boolean;
          latest_parq_response_id: string | null;
          has_active_clearance: boolean;
          active_clearance_id: string | null;
          valid_waivers_count: number;
          required_waivers_count: number;
          latest_assessment_score: number | null;
          latest_assessment_max_score: number | null;
          latest_assessment_date: string | null;
        };

        const parqRequiresClearance =
          onboardingData.parq_has_flags && !onboardingData.has_active_clearance;
        const hasAllRequiredWaivers =
          onboardingData.valid_waivers_count >= onboardingData.required_waivers_count;

        // Calculate completion percentage
        let completedItems = 0;
        let totalItems = 0;

        // Intake forms
        totalItems += onboardingData.intake_forms_total;
        completedItems += onboardingData.intake_forms_completed;

        // PAR-Q (counts as 1 item if required)
        totalItems += 1;
        if (onboardingData.has_completed_parq) completedItems += 1;

        // Waivers
        totalItems += onboardingData.required_waivers_count;
        completedItems += Math.min(
          onboardingData.valid_waivers_count,
          onboardingData.required_waivers_count
        );

        // Medical clearance (only if PAR-Q flags require it)
        if (onboardingData.parq_has_flags) {
          totalItems += 1;
          if (onboardingData.has_active_clearance) completedItems += 1;
        }

        const completionPercentage =
          totalItems > 0 ? Math.round((completedItems / totalItems) * 100) : 100;

        const isComplete =
          completionPercentage === 100 &&
          (!parqRequiresClearance || onboardingData.has_active_clearance);

        setStatus({
          isComplete,
          completionPercentage,
          intakeFormsCompleted: onboardingData.intake_forms_completed,
          intakeFormsTotal: onboardingData.intake_forms_total,
          hasCompletedParq: onboardingData.has_completed_parq,
          parqHasFlags: onboardingData.parq_has_flags,
          parqRequiresClearance,
          latestParqResponseId: onboardingData.latest_parq_response_id,
          hasActiveClearance: onboardingData.has_active_clearance,
          clearanceRequired: parqRequiresClearance,
          activeClearanceId: onboardingData.active_clearance_id,
          validWaiversCount: onboardingData.valid_waivers_count,
          requiredWaiversCount: onboardingData.required_waivers_count,
          hasAllRequiredWaivers,
          hasCompletedAssessment: onboardingData.latest_assessment_score !== null,
          latestAssessmentScore: onboardingData.latest_assessment_score,
          latestAssessmentMaxScore: onboardingData.latest_assessment_max_score,
          latestAssessmentDate: onboardingData.latest_assessment_date,
        });
      } else {
        setStatus(DEFAULT_STATUS);
      }
    } catch (err) {
      console.error("Error fetching onboarding status:", err);
      setError("Failed to load onboarding status");
      setStatus(DEFAULT_STATUS);
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  return {
    status,
    loading,
    error,
    refetch: fetchStatus,
  };
}
